// supervisor-abort-after-merge.ts
//
// Tách THUẦN từ supervisor-release-deploy-verify.ts (phase 10, lô A): abort
// quan sát được SAU KHI merge/deploy đã chạy — luôn đi STOPPING -> reconcile
// -> (rolling_back | waiting_human) -> chỉ TRÚC ĐÓ, khi rollback thành công,
// ABORTED kèm receipt (test 46-49).

import type { MissionStore } from "./mission-store.js";
import {
  type EnvironmentState,
  enterStoppingForAbortAfterMerge,
  type RollbackProcedure,
  reconcileAfterStopping,
} from "./rollback-coordinator.js";
import { finalizeAborted, runApprovedRollback } from "./rollback-execution.js";

export interface AbortAfterMergeInput {
  store: MissionStore;
  missionId: string;
  reason: string;
  procedure: RollbackProcedure | undefined;
  environment: EnvironmentState;
  /** Actual rollback side effect — caller supplies the real recipe runner. */
  runRollbackRecipe: () => Promise<{ ok: boolean; detail: string }>;
}

export type AbortAfterMergeResult =
  | { status: "aborted" }
  | { status: "waiting_human"; reason: string };

/**
 * Test 46-49: abort observed AFTER merge/deploy always goes STOPPING ->
 * reconcile -> (rolling_back | waiting_human) -> only THEN, on rollback
 * success, ABORTED with a written receipt. Rollback failure never claims
 * "base untouched" — it hands off to waiting_human instead.
 */
export async function handleAbortAfterMerge(
  input: AbortAfterMergeInput,
): Promise<AbortAfterMergeResult> {
  const stopping = await enterStoppingForAbortAfterMerge({
    store: input.store,
    missionId: input.missionId,
    reason: input.reason,
  });
  const decision = await reconcileAfterStopping({
    store: input.store,
    stoppingOperationId: stopping.operationId,
    procedure: input.procedure,
    environment: input.environment,
  });

  if (decision.next === "waiting_human") {
    return { status: "waiting_human", reason: decision.reason };
  }

  const rollback = await runApprovedRollback({
    store: input.store,
    missionId: input.missionId,
    authorityRef: decision.authorityRef,
    runRollbackRecipe: input.runRollbackRecipe,
  });
  if (rollback.status === "rollback_failed") {
    return { status: "waiting_human", reason: rollback.reason };
  }

  await finalizeAborted({
    store: input.store,
    missionId: input.missionId,
    consequencesHandled: { kind: "rolled_back", operationId: rollback.operationId },
  });
  return { status: "aborted" };
}
